/* @flow */
import cx from 'classnames';
import { EventEmitter } from 'events';
import invariant from 'invariant';
import PropTypes from 'prop-types';
import * as React from 'react';
import ReactDOM from 'react-dom';

import { getPropClassesFromInstance } from 'lib/getPropClasses';
import createChainedFunction from 'utils/createChainedFunction';
import Box from 'components/Box.jsx';
import Button from 'components/Button.jsx';
import EventTracking, { getEventProperties } from 'lib/EventTracking';
import Glyph from 'components/Glyph.jsx';
import ScrollContext from 'components/ScrollContext.jsx';
import withLayerAutoFocus from 'components/lib/withLayerAutoFocus';
import withLayerHideOnEscapeTrigger from 'components/lib/withLayerHideOnEscapeTrigger';
import withLayerHideOnTransitionTrigger from 'components/lib/withLayerHideOnTransitionTrigger';

import './styles/Dialog.scss';

import type { Props as BoxProps } from 'components/Box.jsx'; // eslint-disable-line no-unused-vars

const stack = [];
// always have enough room for close button
const PADDING_TOP_MIN = 42;

// prettier-ignore
export type Props = {
  box?: boolean,
  children?: React.Node,
  className?: string,
  eventProperties?: Object,
  eventScreenName?: string,
  eventKey?: string,
  full?: boolean,
  hideCloseButton: boolean,
  hideOnBlur?: boolean,
  hideOnEscape?: boolean,
  hidePreviousButton?: boolean,
  size?: 'xlarge' | 'large' | 'medium' | 'small' | 'xsmall',
  theme?: 'default' | 'dark' | 'light',
  onBackdropClick?: (event: SyntheticEvent<>) => void,
  onClick?: (event: SyntheticEvent<>) => void,
  onDialogDismiss?: Function,
  onRequestHide?: () => void,
  onRequestPrevious?: Function,
  /* :: ...$Exact<BoxProps>, */
};

/**
 * ## Modal window
 * ### Dialogs are used to display content in a layer above the app.
 *
 * # Common usage
 *
 * ```example
 * <ModalTrigger
 *   modal={
 *     <Dialog
 *       title="Dialog title"
 *       subtext="Dialog subtext">
 *       <BoxBody>
 *         Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt
 *         ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation
 *         ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in
 *         reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.
 *       </BoxBody>
 *     </Dialog>
 *   }>
 *   <Button>Show Dialog</Button>
 * </ModalTrigger>
 * ```
 *
 * # Required props
 * The Dialog must have either `onRequestHide` passed to it, or have `hideOnEscape={false}`,
 *
 * ```example
 * <ModalTrigger modal={<Dialog />}>
 *   <Button>Show Dialog</Button>
 * </ModalTrigger>
 * ```
 */

class Dialog extends React.Component<Props, { paddingTop: ?number }> {
  static contextTypes = {
    eventProperties: PropTypes.object,
  };

  static propClasses = {
    size: {
      xlarge: 'Dialog--xlarge',
      large: 'Dialog--large',
      medium: 'Dialog--medium',
      small: 'Dialog--small',
      xsmall: 'Dialog--xsmall',
    },
  };

  static defaultProps = {
    box: true,
    hideCloseButton: false,
    hideOnBlur: true,
    hideOnEscape: false,
    hidePreviousButton: true,
    size: 'medium',
    theme: 'default',
    onDialogDismiss: null,
    onRequestPrevious: () => {},
  };

  static _emitter: EventEmitter = new EventEmitter();

  static getCurrent() {
    return stack[stack.length - 1];
  }

  static on(event: string, listener: Function) {
    Dialog._emitter.addListener(event, listener);
  }

  static removeListener(event: string, listener: Function) {
    Dialog._emitter.removeListener(event, listener);
  }

  state = {
    paddingTop: null,
  };

  componentDidMount() {
    invariant(
      this.props.onRequestHide || !this.props.hideOnEscape, // eslint-disable-line react/prop-types
      'Dialog must have onRequestHide or hideOnEscape={false}'
    );

    stack.push(this);
    Dialog._emitter.emit('show');

    const { eventProperties, eventKey, eventScreenName, full } = this.props;

    if (eventScreenName) {
      const computedEventProperties = getEventProperties(eventProperties, this.context);
      EventTracking.view(eventScreenName, computedEventProperties);
    }

    if (eventKey) {
      EventTracking.trackKey(eventKey);
    }

    if (!full) {
      window.addEventListener('resize', this.resetPosition, false);
      this._dialogTimeout = setTimeout(() => this.resetPosition(), 0);
    }
  }

  componentWillUnmount() {
    const index = stack.indexOf(this);

    if (index >= 0) {
      stack.splice(index, 1);
    }

    Dialog._emitter.emit('hide');

    if (!this.props.full) {
      window.removeEventListener('resize', this.resetPosition, false);
      window.clearTimeout(this._dialogTimeout);
    }
  }

  closeDialog = (event: SyntheticEvent<>) => {
    const content = ReactDOM.findDOMNode(this.refs.content);

    // in IE only, content is somehow sometimes null
    // https://my.trackjs.com/messages/filtered?hash=c677c309de472679
    // $FlowFixMe
    if (content && content.contains(event.target)) {
      return;
    }

    event.stopPropagation();
    if (this.props.onDialogDismiss) {
      this.props.onDialogDismiss();
    } else if (this.props.onRequestHide) {
      this.props.onRequestHide();
    }
  };

  _dialogTimeout = null;

  handleBackdropClick = (event: SyntheticEvent<>) => {
    if (!this.props.hideOnBlur) {
      return;
    }

    // $FlowFixMe
    if (event.target === event.currentElement) {
      if (this.props.onRequestHide) {
        this.props.onRequestHide();
      }
    }
  };

  handleWrapClick = (event: SyntheticEvent<>) => {
    const content = ReactDOM.findDOMNode(this.refs.content);

    // $FlowFixMe
    if (!this.props.hideOnBlur || content.contains(event.target)) {
      return;
    }

    if (this.props.onDialogDismiss) {
      this.props.onDialogDismiss();
    } else if (this.props.onRequestHide) {
      this.props.onRequestHide();
    }
  };

  resetPosition = () => {
    const root = ReactDOM.findDOMNode(this);
    const container = ReactDOM.findDOMNode(this.refs.container);
    // $FlowFixMe
    const offsetTop = (root.offsetHeight - container.offsetHeight) / 2;
    const paddingTop = offsetTop > PADDING_TOP_MIN ? offsetTop : PADDING_TOP_MIN;

    this.setState({ paddingTop });
  };

  renderContent() {
    /* eslint-disable */
    const {
      box,
      children,
      full,
      hideCloseButton,
      hideOnBlur,
      hideOnEscape,
      hidePreviousButton,
      size,
      onDialogDismiss,
      onRequestHide,
      onRequestPrevious,
      ...props
    } = this.props;
    /* eslint-enable */

    if (box) {
      return (
        <Box {...props} onImageLoad={full ? null : this.resetPosition} className="Dialog-box">
          {!hidePreviousButton && (
            <Button
              aria-label="Back"
              color="brand"
              level="icon"
              className="Dialog-previousButton"
              eventElementName="Dialog-previousButton"
              onClick={onRequestPrevious}>
              <Glyph name="arrow-left" size="large" solo />
            </Button>
          )}
          {children}
        </Box>
      );
    } else {
      return children;
    }
  }

  renderBody() {
    const { full, hideCloseButton } = this.props;
    const { paddingTop } = this.state;

    if (full) {
      return this.renderContent();
    } else {
      return (
        <div style={paddingTop ? {} : { opacity: 0.01 }} className="Dialog-inner">
          <div style={{ paddingTop }} className="Dialog-wrap" onClick={this.handleWrapClick}>
            <div ref="container" className="Dialog-container" onClick={e => e.stopPropagation()}>
              <div ref="content">{this.renderContent()}</div>
              {!hideCloseButton && (
                <Button
                  aria-label="Close dialog"
                  color="white"
                  level="icon"
                  className="Dialog-closeButton"
                  eventElementName="Dialog-closeButton"
                  onClick={this.closeDialog}>
                  <Glyph name="close" size="large" solo />
                </Button>
              )}
            </div>
          </div>
        </div>
      );
    }
  }

  render() {
    const { className, full, onClick, theme } = this.props;

    return (
      <ScrollContext
        tabIndex={0}
        className={cx('Dialog-backdrop', {
          'Dialog-backdrop--dark': theme === 'dark',
          'Dialog-backdrop--light': theme === 'light',
        })}
        onClick={createChainedFunction(onClick, this.handleBackdropClick)}
        data-test-selector="dialog">
        <div
          className={cx(
            'Dialog',
            getPropClassesFromInstance(this),
            {
              'Dialog--full': full,
            },
            className
          )}>
          {this.renderBody()}
        </div>
      </ScrollContext>
    );
  }
}

export { Dialog };
export default withLayerAutoFocus(
  withLayerHideOnTransitionTrigger(withLayerHideOnEscapeTrigger(Dialog))
);
